/* Read-only cart offer. Shopify's existing gift controller owns cart mutations. */
(function (scope) {
  const offerSelector = '[data-emery-mold-cart-offer]';
  const drawerStructure = '#cart-drawer, .cart-drawer__inner, .cart-drawer__line-items, .cart-drawer__recommendations';
  const fallbackCollection = '/collections/stackable-treat-molds';
  const stylesheet = `
#cart-drawer [data-emery-mold-cart-offer] {
  padding: 15px 18px; border-radius: 18px; color: #162329;
  background: linear-gradient(100deg, #e8f6ff, #f4edff);
  box-shadow: 0 4px 14px -5px rgb(164 156 255 / 19%);
  text-align: center; font-size: 14px; line-height: 1.4;
}
#cart-drawer [data-emery-mold-cart-offer] p { margin: 0; }
#cart-drawer [data-emery-mold-cart-offer] strong { font-weight: 700; }
#cart-drawer [data-emery-mold-cart-offer] .emery-cart-offer__shop { display: inline-block; margin-top: 8px; text-decoration: underline dotted; text-underline-offset: 3px; }
#cart-drawer [data-emery-mold-cart-offer] [hidden] { display: none !important; }
`;

  function calculateOffer(cart, giftConfig = {}, offerConfig = {}) {
    if (!Array.isArray(cart?.items) || giftConfig.enabled !== true || giftConfig.available !== true) return null;
    const products = new Set((giftConfig.products || []).map(product => String(product.id)));
    const count = cart.items.reduce((sum, item) => sum + (
      item.properties?._emery_mold_gift !== 'two-molds' && products.has(String(item.product_id)) ? Number(item.quantity) || 0 : 0
    ), 0);
    // Once qualified, the normal free-container line communicates the reward.
    if (count < 1 || count >= 2) return null;
    const remaining = 2 - count;
    const url = offerConfig.collectionUrl;
    return {
      count, remaining,
      giftText: `Add ${remaining} more ${remaining === 1 ? 'mold' : 'molds'} for a free container`,
      collectionUrl: typeof url === 'string' && url.startsWith('/') && !url.startsWith('//') ? url : fallbackCollection
    };
  }

  function shouldRenderMutations(records) {
    return records.some(record => {
      // Our text updates and banner insertion must not schedule themselves.
      if (record.target?.closest?.(offerSelector)) return false;
      const nodes = [...(record.addedNodes || []), ...(record.removedNodes || [])];
      return nodes.some(node => node.nodeType === 1 && !node.matches(offerSelector) && (
        node.matches(drawerStructure) || node.querySelector(drawerStructure)
      ));
    });
  }

  function createDrawerRenderer(document) {
    return state => {
      const drawer = document.getElementById('cart-drawer');
      if (!drawer) return;
      let banner = drawer.querySelector(offerSelector);
      const lines = drawer.querySelector('.cart-drawer__line-items');
      if (!state || !lines) { banner?.remove(); return; }
      if (!document.getElementById('emery-mold-cart-offer-style')) {
        const style = document.createElement('style');
        style.id = 'emery-mold-cart-offer-style';
        style.textContent = stylesheet;
        document.head.append(style);
      }
      if (!banner) {
        banner = document.createElement('aside');
        banner.setAttribute('data-emery-mold-cart-offer', '');
        banner.setAttribute('aria-label', 'Treat mold offer');
        banner.setAttribute('aria-live', 'polite');
        const title = document.createElement('p');
        title.append(document.createElement('strong'));
        const shop = document.createElement('a');
        shop.className = 'emery-cart-offer__shop';
        shop.textContent = 'Shop molds →';
        banner.append(title, shop);
      }
      const title = banner.querySelector('strong');
      const shop = banner.querySelector('.emery-cart-offer__shop');
      if (title.textContent !== state.giftText) title.textContent = state.giftText;
      if (shop.getAttribute('href') !== state.collectionUrl) shop.setAttribute('href', state.collectionUrl);
      const recommendations = drawer.querySelector('.cart-drawer__recommendations');
      if (recommendations) {
        if (banner.nextElementSibling !== recommendations) recommendations.before(banner);
      } else if (lines.nextElementSibling !== banner) {
        lines.after(banner);
      }
    };
  }

  function createController(env) {
    let cart = null;
    let revision = 0;
    let started = false;
    let renderQueued = false;
    const renderView = env.render || createDrawerRenderer(env.document);
    const render = () => renderView(calculateOffer(cart, env.getGiftConfig(), env.getOfferConfig()));
    function scheduleRender() {
      if (renderQueued) return;
      renderQueued = true;
      Promise.resolve().then(() => { renderQueued = false; render(); });
    }
    async function refresh() {
      const requestRevision = ++revision;
      try {
        const response = await env.fetch(`${env.root()}cart.js`, { cache: 'no-store' });
        if (!response.ok) return;
        const received = await response.json();
        if (requestRevision !== revision || !Array.isArray(received?.items)) return;
        cart = received;
        render();
      } catch { /* Keep the last known offer until a later cart update succeeds. */ }
    }
    function onCartChange(event) {
      // Cart events supersede any older initial/refresh request still in flight.
      revision += 1;
      if (Array.isArray(event.detail?.cart?.items)) {
        cart = event.detail.cart;
        render();
      } else refresh();
    }
    function start() {
      if (started) return;
      started = true;
      env.document.addEventListener('cart:change', onCartChange);
      env.document.addEventListener('cart:refresh', refresh);
      env.window.addEventListener('pageshow', event => { if (event.persisted) refresh(); });
      new env.window.MutationObserver(records => {
        if (shouldRenderMutations(records)) scheduleRender();
      }).observe(env.document.body, { childList: true, subtree: true });
      refresh();
    }
    return { start, refresh };
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { calculateOffer, shouldRenderMutations, createDrawerRenderer, createController };
  } else if (!scope.EmeryTreatMoldCartOffer) {
    const readConfig = id => {
      try { return JSON.parse(scope.document.getElementById(id)?.textContent || '{}'); }
      catch { return {}; }
    };
    scope.EmeryTreatMoldCartOffer = createController({
      window: scope, document: scope.document, fetch: scope.fetch.bind(scope),
      root: () => scope.Shopify?.routes.root || '/',
      getGiftConfig: () => readConfig('treat-mold-gift-config'),
      getOfferConfig: () => readConfig('emery-mold-cart-offer-config')
    });
    if (scope.document.readyState === 'loading') {
      scope.document.addEventListener('DOMContentLoaded', () => scope.EmeryTreatMoldCartOffer.start(), { once: true });
    } else scope.EmeryTreatMoldCartOffer.start();
  }
})(typeof window === 'undefined' ? globalThis : window);
